import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Fires a fire-and-forget ping to the track-visit Netlify Function on every
 * route change (pathname only, hash-only jumps like #about don't count as a
 * new page). Sends the path, whether this is the first page of the tab's
 * visit, the outside referrer that visit started from, and the visitor's
 * browser timezone (not IP-derived, just Intl's resolved timezone), the
 * server fills in the UTC timestamp itself. Silently does nothing on
 * localhost so local development never sends real text messages.
 */
export default function useVisitTracker() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const isLocal = ["localhost", "127.0.0.1"].includes(window.location.hostname);
    if (isLocal) return;

    let tz = "";
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    } catch {
      // ignore, tz stays empty
    }

    // The first page of this tab's visit keeps its outside referrer; later
    // pages resend it so the text still says where the visitor came from.
    let first = true;
    let ref = document.referrer || "";
    try {
      const saved = sessionStorage.getItem("tv-ref");
      if (saved !== null) {
        first = false;
        ref = saved;
      } else {
        sessionStorage.setItem("tv-ref", ref);
      }
    } catch {
      // storage blocked, every page reads as a new visit
    }

    fetch("/.netlify/functions/track-visit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path: pathname, first, tz, ref }),
      keepalive: true,
    }).catch(() => {
      // Visit tracking is best-effort; never let it affect the page.
    });
  }, [pathname]);
}
